import React, { useState, useEffect } from "react";

const SubjectExamLinkForm = ({ subject, exams, onSubmit, onClose }) => {
  const [linkedExams, setLinkedExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initialize with currently linked exams
  useEffect(() => {
    if (subject && exams) {
      const currentlyLinked = (subject.exams || [])
        .map(examRef => {
          const examId = examRef._id || examRef;
          const exam = exams.find(e => e._id === examId);
          if (!exam) return null;
          
          const pricing = subject.examPricing?.find(p => 
            (p.exam?._id || p.exam) === examId
          );
          
          return {
            examId: examId,
            exam: exam,
            isLinked: true,
            pricing: pricing ? {
              isPurchasable: pricing.isPurchasable !== false,
              price: pricing.price || 0,
              discountPrice: pricing.discountPrice || '',
              testsOnlyPrice: pricing.testsOnlyPrice || '',
              testsOnlyDiscountPrice: pricing.testsOnlyDiscountPrice || '',
              isFree: pricing.isFree || false
            } : {
              isPurchasable: true,
              price: 0,
              discountPrice: '',
              testsOnlyPrice: '',
              testsOnlyDiscountPrice: '',
              isFree: false
            }
          };
        })
        .filter(Boolean);
      
      setLinkedExams(currentlyLinked);
    }
  }, [subject, exams]);

  // Filter exams based on search
  const filteredExams = exams.filter(exam =>
    exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    exam.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle exam link
  const toggleExamLink = (exam) => {
    setLinkedExams(prev => {
      const existing = prev.find(e => e.examId === exam._id);
      if (existing) {
        // Unlink exam
        return prev.filter(e => e.examId !== exam._id);
      } else {
        // Link with default pricing
        return [...prev, {
          examId: exam._id,
          exam: exam,
          isLinked: false,
          pricing: {
            isPurchasable: true,
            price: 0,
            discountPrice: '',
            testsOnlyPrice: '',
            testsOnlyDiscountPrice: '',
            isFree: false
          }
        }];
      }
    });
  };

  // Update pricing for an exam
  const updatePricing = (examId, field, value) => {
    setLinkedExams(prev => prev.map(item => {
      if (item.examId === examId) {
        return {
          ...item,
          pricing: {
            ...item.pricing,
            [field]: field === 'isFree' || field === 'isPurchasable' 
              ? value 
              : (value === '' ? '' : Number(value))
          }
        };
      }
      return item;
    }));
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);

    try {
      const result = await onSubmit(subject._id, linkedExams);
      if (result?.success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h4 className="modal-title">Link {subject?.title} to Exams</h4>
            <p className="text-13 text-light-1 mt-5">
              Select exams and set pricing for this subject in each exam
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit} className="content-form">
            {/* Search */}
            <div className="form-group mb-20">
              <input
                type="text"
                placeholder="Search exams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ padding: '10px 15px' }}
              />
            </div>

            {/* Exams List */}
            <div className="exams-link-list" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredExams.length === 0 ? (
                <div className="text-center py-30 text-13 text-light-2">
                  <i className="icon-search text-30 mb-10"></i>
                  <p>No exams found</p>
                </div>
              ) : (
                filteredExams.map((exam) => {
                  const linkedItem = linkedExams.find(e => e.examId === exam._id);
                  const isLinked = !!linkedItem;

                  return (
                    <div key={exam._id} className="exam-link-item bg-light-5 rounded-8 mb-15 border border-light-3" style={{ padding: '20px' }}>
                      {/* Exam Header */}
                      <div className="d-flex items-center justify-between mb-15">
                        <div className="d-flex items-center flex-1">
                          <label className="checkbox-label" style={{ margin: 0, cursor: 'pointer', flex: 1 }}>
                            <input
                              type="checkbox"
                              checked={isLinked}
                              onChange={() => toggleExamLink(exam)}
                              className="form-checkbox mr-10"
                            />
                            <div className="d-flex items-center">
                              <div 
                                className="size-35 d-flex justify-center items-center rounded-6 mr-10"
                                style={{backgroundColor: exam.color + '20', color: exam.color}}
                              >
                                <i className={`text-14 ${exam.icon || 'icon-graduation-cap'}`}></i>
                              </div>
                              <div>
                                <div className="text-14 fw-500">{exam.title}</div>
                                <div className="text-12 text-light-2">{exam.description || 'No description'}</div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Pricing Form (shown when linked) */}
                      {isLinked && (
                        <div className="pricing-form bg-white rounded-6 border border-light-3" style={{ padding: '20px', marginTop: '15px' }}>
                          <h6 className="text-13 fw-500 mb-15">Pricing for this Exam</h6>
                          
                          <div className="form-row mb-15">
                            <div className="form-group half">
                              <div className="checkbox-group">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={linkedItem.pricing.isFree}
                                    onChange={(e) => updatePricing(exam._id, 'isFree', e.target.checked)}
                                    className="form-checkbox"
                                  />
                                  <span className="checkbox-text">Free Subject</span>
                                </label>
                              </div>
                            </div>
                            <div className="form-group half">
                              <div className="checkbox-group">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={linkedItem.pricing.isPurchasable}
                                    onChange={(e) => updatePricing(exam._id, 'isPurchasable', e.target.checked)}
                                    disabled={linkedItem.pricing.isFree}
                                    className="form-checkbox"
                                  />
                                  <span className="checkbox-text">Available for Purchase</span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {!linkedItem.pricing.isFree && linkedItem.pricing.isPurchasable && (
                            <>
                              <div className="form-row mb-15">
                                <div className="form-group half">
                                  <label className="form-label">Full Access Price (₹) *</label>
                                  <input
                                    type="number"
                                    value={linkedItem.pricing.price}
                                    onChange={(e) => updatePricing(exam._id, 'price', e.target.value)}
                                    className="form-input"
                                    min="0"
                                    required={linkedItem.pricing.isPurchasable && !linkedItem.pricing.isFree}
                                    placeholder="e.g., 500"
                                  />
                                </div>
                                <div className="form-group half">
                                  <label className="form-label">Discount Price (₹)</label>
                                  <input
                                    type="number"
                                    value={linkedItem.pricing.discountPrice}
                                    onChange={(e) => updatePricing(exam._id, 'discountPrice', e.target.value)}
                                    className="form-input"
                                    min="0"
                                    placeholder="e.g., 400"
                                  />
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-group half">
                                  <label className="form-label">Tests-Only Price (₹)</label>
                                  <input
                                    type="number"
                                    value={linkedItem.pricing.testsOnlyPrice}
                                    onChange={(e) => updatePricing(exam._id, 'testsOnlyPrice', e.target.value)}
                                    className="form-input"
                                    min="0"
                                    placeholder="e.g., 250"
                                  />
                                </div>
                                <div className="form-group half">
                                  <label className="form-label">Tests-Only Discount (₹)</label>
                                  <input
                                    type="number"
                                    value={linkedItem.pricing.testsOnlyDiscountPrice}
                                    onChange={(e) => updatePricing(exam._id, 'testsOnlyDiscountPrice', e.target.value)}
                                    className="form-input"
                                    min="0"
                                    placeholder="e.g., 200"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <div className="modal-footer mt-20">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-primary bg-purple"
              >
                {submitting ? 'Saving...' : linkedExams.length === 0 ? 'Save (No Exams Linked)' : `Link to ${linkedExams.length} Exam${linkedExams.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubjectExamLinkForm;

